import type { User } from "./response";

// -------- Verification method
export enum CashCheckVerificationMethod {
  SCAN = 1,
  MANUAL = 2,
  SCALE_SCAN = 3,
  MANUAL_WEIGHT = 4,
  WEIGHT_FINALIZATION = 5,
}

export const getCashCheckVerificationMethodName = (
  method: CashCheckVerificationMethod,
) => {
  switch (method) {
    case CashCheckVerificationMethod.SCAN:
      return "cash_check_method_scan";
    case CashCheckVerificationMethod.MANUAL:
      return "cash_check_method_manual";
    case CashCheckVerificationMethod.SCALE_SCAN:
      return "cash_check_method_scale_scan";
    case CashCheckVerificationMethod.MANUAL_WEIGHT:
      return "cash_check_method_manual_weight";
    case CashCheckVerificationMethod.WEIGHT_FINALIZATION:
      return "cash_check_method_weight_finalization";
    default:
      return "cash_check_method_scan";
  }
};

export const getCashCheckVerificationMethodColor = (
  method: CashCheckVerificationMethod,
) => {
  switch (method) {
    case CashCheckVerificationMethod.SCAN:
      return "green";
    case CashCheckVerificationMethod.MANUAL:
      return "orange";
    case CashCheckVerificationMethod.SCALE_SCAN:
      return "blue";
    case CashCheckVerificationMethod.MANUAL_WEIGHT:
      return "volcano";
    case CashCheckVerificationMethod.WEIGHT_FINALIZATION:
      return "purple";
    default:
      return "default";
  }
};

/** A verification that carried a weight measurement — undoing one restores the
 * previous quantity and price, so the whole order must be re-fetched after. */
export const isWeightVerificationMethod = (
  method: CashCheckVerificationMethod,
) =>
  method === CashCheckVerificationMethod.SCALE_SCAN ||
  method === CashCheckVerificationMethod.MANUAL_WEIGHT ||
  method === CashCheckVerificationMethod.WEIGHT_FINALIZATION;

export const isPendingAutomaticScaleScan = (
  verification: Pick<
    ICashCheckVerification,
    "method" | "finalizationVerificationId" | "isSystemFinalization"
  >,
): boolean =>
  verification.method === CashCheckVerificationMethod.SCALE_SCAN &&
  verification.finalizationVerificationId == null &&
  !verification.isSystemFinalization;

// -------- Line verification mode
export type CashCheckVerificationMode =
  | "WEIGHT"
  | "WEIGHT_PACKAGES"
  | "WEIGHT_SCANS"
  | "PER_PIECE"
  | "LINE";

export const isWeightVerificationMode = (
  mode?: CashCheckVerificationMode | null,
): boolean =>
  mode === "WEIGHT" || mode === "WEIGHT_PACKAGES" || mode === "WEIGHT_SCANS";

export const getCashCheckVerificationModeName = (
  mode?: CashCheckVerificationMode | null,
) => {
  switch (mode) {
    case "WEIGHT":
    case "WEIGHT_PACKAGES":
    case "WEIGHT_SCANS":
      return "cash_check_mode_weight";
    case "PER_PIECE":
      return "cash_check_mode_per_piece";
    case "LINE":
      return "cash_check_mode_line";
    default:
      return "cash_check_mode_line";
  }
};

export const getCashCheckVerificationModeColor = (
  mode?: CashCheckVerificationMode | null,
) => {
  switch (mode) {
    case "WEIGHT":
    case "WEIGHT_PACKAGES":
    case "WEIGHT_SCANS":
      return "blue";
    case "PER_PIECE":
      return "geekblue";
    default:
      return "default";
  }
};

// -------- Manual verification reason
export enum CashCheckManualReason {
  EMERGENCY = 1,
  NO_BARCODE = 2,
}

export const getCashCheckManualReasonName = (reason: CashCheckManualReason) => {
  switch (reason) {
    case CashCheckManualReason.EMERGENCY:
      return "cash_check_manual_reason_emergency";
    case CashCheckManualReason.NO_BARCODE:
      return "cash_check_manual_reason_no_barcode";
    default:
      return "cash_check_manual_reason_emergency";
  }
};

// -------- 422 error keys returned in `errorKey` alongside translated `errors[]`
export enum CashCheckErrorKey {
  BARCODE_UNKNOWN = "cash_check_barcode_unknown",
  PRODUCT_NOT_IN_ORDER = "cash_check_product_not_in_order",
  LINE_ALREADY_COMPLETE = "cash_check_line_already_complete",
  SCAN_TOKEN_CONFLICT = "cash_check_scan_token_conflict",
  MUST_BE_ACCOUNTING_FINALIZATION = "must_be_accounting_finalization",
  DISABLED = "cash_check_disabled",
  INCOMPLETE = "cash_check_incomplete",
  // Scale barcode
  SCALE_LINE_AMBIGUOUS = "cash_check_scale_line_ambiguous",
  SCALE_BARCODE_INVALID = "cash_check_scale_barcode_invalid",
  SCALE_BARCODE_REQUIRED = "cash_check_scale_barcode_required",
  SCALE_PRODUCT_NOT_WEIGHTED = "cash_check_scale_product_not_weighted",
  MANUAL_WEIGHT_REQUIRED = "cash_check_manual_weight_required",
  WEIGHT_PACKAGES_REQUIRED = "cash_check_weight_packages_required",
  WEIGHT_PACKAGES_INVALID = "cash_check_weight_packages_invalid",
  WEIGHT_PACKAGE_WRONG_PRODUCT = "cash_check_weight_package_wrong_product",
  SCALE_UNDO_NOT_LATEST = "cash_check_scale_undo_not_latest",
  SCALE_VERIFICATION_STALE = "cash_check_scale_verification_stale",
  SCALE_UNDO_INVENTORY_UNAVAILABLE = "cash_check_scale_undo_inventory_unavailable",
  SCALE_FINANCIAL_SETTLEMENT_LOCKED = "cash_check_scale_financial_settlement_locked",
  INVENTORY_INVARIANT_FAILED = "cash_check_inventory_invariant_failed",
  WEIGHT_MODE_CONFLICT = "cash_check_weight_mode_conflict",
  WEIGHT_FINALIZATION_CANNOT_BE_UNDONE = "cash_check_weight_finalization_cannot_be_undone",
  ACCOUNTING_ALREADY_FINISHED = "accounting_already_finished",
  QUANTITY_VARIANCE = "cash_check_quantity_variance",
}

/** One line whose scanned quantity drifted beyond its tolerance. Quantities are
 * integer grams for `GRAM` lines and piece counts for `PIECE` lines. */
export interface ICashCheckQuantityVarianceLine {
  productOrderId: number;
  /** The actual product (the substitute when the line was replaced). */
  productId: number;
  isWeighted: boolean;
  quantityUnit: "GRAM" | "PIECE";
  requestedQuantity: number;
  scannedQuantity: number;
  /** `scannedQuantity - requestedQuantity`. */
  difference: number;
  /** Allowed drift in grams. */
  tolerance: number;
  direction: "UNDER" | "OVER";
}

/** `context` of a `cash_check_quantity_variance` refusal of
 * mark-accounting-finished. The refusal changes no quantities or stock. */
export interface ICashCheckQuantityVarianceContext {
  lines?: ICashCheckQuantityVarianceLine[];
}

/** `context` of a `cash_check_scale_line_ambiguous` refusal: the same weighted
 * product sits on more than one line, so the operator must pick the intended
 * one and the very same scan is replayed with `productOrderId`. */
export interface ICashCheckAmbiguousContext {
  candidateProductOrderIds?: number[];
}

/**
 * `context` of a `cash_check_barcode_unknown` refusal. The key is thrown from
 * two places and only one of them carries a code: a well-formed 13-digit scale
 * label whose five-digit product code is unregistered. An ordinary unknown
 * barcode arrives with no `context` at all — there is nothing to extract from
 * it — so this must always be read as optional.
 */
export interface ICashCheckUnknownBarcodeContext {
  /** Exactly five digits, leading zeros preserved. Never coerce to a number. */
  parsedProductCode?: string;
}

// -------- Response models
/** Layout of the scale label as configured on the server. It is informational
 * only: the barcode is always forwarded untouched and parsed server-side. */
export interface ICashCheckScaleBarcodeConfig {
  enabledForSoldByUnit: boolean;
  submissionMode?: "PACKAGES" | string | null;
  maximumPackagesPerSubmission?: number | null;
  totalLength: number;
  ignoredLeadingLength: number;
  productBarcodeLength: number;
  weightLength: number;
  ignoredTrailingLength: number;
}

export interface ICashCheckVerification {
  id: number;
  method: CashCheckVerificationMethod;
  methodName?: string | null;
  manualReason?: CashCheckManualReason | null;
  manualReasonName?: string | null;
  scannedBarcode?: string | null;
  /** Product part the server extracted from a scale label. */
  parsedProductBarcode?: string | null;
  finalizationVerificationId?: number | null;
  isSystemFinalization?: boolean;
  packageCount?: number | null;
  /** New responses return package details; string remains accepted for older
   * API records created before the package contract was introduced. */
  scalePackages?: Array<string | ICashCheckSavedScalePackage> | null;
  /** All `*QuantityPhysical` values are integer grams for weighted products. */
  previousQuantityPhysical?: number | null;
  measuredQuantityPhysical?: number | null;
  factorAccountingSystem?: number | null;
  inventoryCoveredQuantityPhysical?: number | null;
  shortageCreatedQuantityPhysical?: number | null;
  verifiedBy?: User | null;
  verifiedAt: string | null;
}

export interface ICashCheckSavedScalePackage {
  barcode: string;
  itemToken?: string | null;
  parsedProductBarcode?: string | null;
  measuredQuantityPhysical?: number | null;
}

/** Outcome of a weight-carrying verification. Any non-null value means the
 * order quantities, prices, coupon and delivery may have changed server-side. */
export interface ICashCheckWeightAdjustment {
  verificationId: number;
  /** Immutable quantity originally requested by the customer. */
  requestedQuantityPhysical?: number | null;
  /** Applied weight before this pending cycle. */
  previousQuantityPhysical: number | null;
  measuredQuantityPhysical: number;
  /** `measuredQuantityPhysical - previousQuantityPhysical`. */
  deltaQuantityPhysical: number | null;
  /** Explicit alias for the pending cycle delta. */
  pendingDeltaQuantityPhysical?: number | null;
  factorAccountingSystem: number | null;
  inventoryCoveredQuantityPhysical: number;
  shortageCreatedQuantityPhysical: number;
  packageCount?: number | null;
  packageMeasuredQuantityPhysical?: number | null;
  pendingFinalization?: boolean;
}

export interface ICashCheckShortageItem {
  id: number;
  productOrderId: number;
  productId: number;
  storeId: number;
  status: number;
  statusName?: string | null;
  initialQuantityPhysical: number;
  remainingQuantityPhysical: number;
  coveredQuantityPhysical: number;
}

export interface ICashCheckShortageSummary {
  hasOpenShortage: boolean;
  openCount: number;
  openQuantityPhysical: number;
  coveredQuantityPhysical: number;
  cancelledQuantityPhysical: number;
  varianceQuantityPhysical: number;
  items: ICashCheckShortageItem[];
}

/**
 * Verification state of one order line. Display data (name, image, quantity,
 * badges) is NOT part of this contract — it is derived by joining
 * `productOrderId` against the order details' `orderProducts`.
 */
export interface ICashCheckLine {
  productOrderId: number;
  /** WEIGHT_SCANS: incremental automatic labels. WEIGHT: manual/legacy total.
   * PER_PIECE: a scan per piece. LINE: a single scan for the whole line. */
  verificationMode?: CashCheckVerificationMode;
  requiredVerifications: number;
  verifiedCount: number;
  isComplete: boolean;
  isOverVerified: boolean;
  verifications: ICashCheckVerification[];
  /** Resolved barcode list for the line's actual product in its own store. */
  barcodes: string[];
  /** Integer grams on weighted lines. */
  requestedQuantityPhysical?: number | null;
  actualQuantityPhysical?: number | null;
  /** Backward-compatible alias of `requestedQuantityPhysical`. */
  expectedQuantityPhysical?: number | null;
  allocatedQuantityPhysical?: number | null;
  shortageQuantityPhysical?: number | null;
  measuredQuantityPhysical?: number | null;
  /** `measuredQuantityPhysical - requestedQuantityPhysical`. */
  weightDifferencePhysical?: number | null;
  /** `measuredQuantityPhysical - actualQuantityPhysical`. */
  pendingDeltaQuantityPhysical?: number | null;
  packageCount?: number | null;
  hasPendingWeightFinalization?: boolean;
}

export interface IOrderCashCheck {
  enabled: boolean;
  scanPerPiece: boolean;
  isComplete: boolean;
  totalRequired: number;
  totalVerified: number;
  scaleBarcode?: ICashCheckScaleBarcodeConfig | null;
  shortageSummary?: ICashCheckShortageSummary | null;
  lines: ICashCheckLine[];
}

export interface ICashCheckMutationResponse {
  duplicate: boolean;
  /** Note the name differs from the GET payload's `isComplete`. */
  isCheckComplete: boolean;
  line: ICashCheckLine | null;
  weightAdjustment?: ICashCheckWeightAdjustment | null;
  shortageSummary?: ICashCheckShortageSummary | null;
}

// -------- Request models
export interface ICashCheckScanRequest {
  /** Scanner output as a trimmed string. It is never coerced to a number or
   * rewritten client-side, so leading zeros survive. */
  barcode: string;
  scanToken: string;
  /** Sent when replaying a locally or server-detected ambiguous scan after the
   * operator explicitly chooses its intended order line. */
  productOrderId?: number;
}

export interface ICashCheckManualRequest {
  productOrderId: number;
  reason: CashCheckManualReason;
}

export interface ICashCheckManualWeightRequest {
  productOrderId: number;
  /** Integer grams, 1..99999. */
  measuredQuantityPhysical: number;
  reason: CashCheckManualReason;
  operationToken: string;
}

// -------- Weight bounds shared by scale labels and manual entry
export const CASH_CHECK_MIN_WEIGHT_GRAMS = 1;
export const CASH_CHECK_MAX_WEIGHT_GRAMS = 99999;
